import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Empty,
  Pagination,
  Grid,
  Space,
  Switch,
  Table,
  Input,
  Modal,
  Select,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  DownOutlined,
  MoreOutlined,
  ReloadOutlined,
  ExportOutlined,
  EyeInvisibleOutlined,
  UploadOutlined,
  CloseOutlined,
  StarFilled,
  SearchOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { useSelection } from "../SelectionContext";
import { useEcommercePermissions } from "../hooks/useEcommercePermissions";
import { PermissionDenied } from "./components";
import ProductDisplayOrderSettings from "./components/ProductDisplayOrderSettings";
import ProductOnlineSettingsModal from "./ProductOnlineSettingsModal";
import type { OnlineSettingsTriggerProps } from "./productOnlineSettingsModel";
import API_CONFIG from "../config/api.js";
import "./EcommerceAdmin.css";
import { EcommerceFilterField, ProductThumbnail, useEcommerceWorkspaceStyle, useEcommerceTableHeight } from "./EcommerceWorkspace";
import { ecommerceRequestHeaders, ProductOnlinePatch, updateSelectedProducts } from "./ecommerceWorkspaceUtils";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const API_URL = API_CONFIG.BASE_URL;
const FEATURED_PRODUCTS_LIMIT = 10;

interface Product {
  id: number;
  name: string;
  description: string;
  product_type?: string;
  production_kind?: string;
  price: number;
  stock_quantity: number;
  online_visibility: boolean;
  online_price: number;
  online_description: string;
  online_featured: boolean;
  online_seo_title: string;
  online_seo_description: string;
  online_min_quantity: number;
  online_max_quantity: number;
  stock_management_mode?: "standard" | "pre_order" | "unlimited"; // 🆕 Mode de gestion du stock
  image: string;
  image_url: string;
  category_name: string;
  brand_name: string;
  // 🆕 Produit à variantes : la ligne représente le PARENT, le prix/stock sont agrégés
  is_variant_parent?: boolean;
  variants_count?: number;
  price_min?: number;
  price_max?: number;
}

interface FeaturedStore {
  id: number;
  store_name: string;
  store_slug: string;
}

const ProduitsEnLigne: React.FC<OnlineSettingsTriggerProps> = ({ onEditProduct, settingsRevision = 0 }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    visibility: "all",
    featured: "all",
  });
  const [categories, setCategories] = useState<any[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [featuredProductsCount, setFeaturedProductsCount] = useState(0);
  const [featuredStores, setFeaturedStores] = useState<FeaturedStore[]>([]);
  const [selectedFeaturedStoreId, setSelectedFeaturedStoreId] = useState<
    number | null
  >(null);
  const [featuredScopeWarehouseId, setFeaturedScopeWarehouseId] = useState<
    number | null
  >(null);
  const [featuredScopeError, setFeaturedScopeError] = useState("");
  const [scopeRevision, setScopeRevision] = useState(0);
  const isMountedRef = useRef(true);
  const productsRequestIdRef = useRef(0);
  const categoriesRequestIdRef = useRef(0);
  const productsAbortRef = useRef<AbortController | null>(null);
  const productMutationBusyRef = useRef(false);
  const [productsError, setProductsError] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [batchResult, setBatchResult] = useState<{ type: "success" | "warning"; message: string; description: string } | null>(null);
  const workspaceStyle = useEcommerceWorkspaceStyle();
  const { tableRef, tableHeight } = useEcommerceTableHeight();

  // 🔄 PAGINATION STATE - Clean server-side pagination
  const [paginationState, setPaginationState] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const cataloguePage = paginationState.current;
  const cataloguePageSize = paginationState.pageSize;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const { selectedWarehouse, selectedCompany } = useSelection();
  const scopeKey = `${selectedCompany || ""}:${selectedWarehouse || ""}:${selectedFeaturedStoreId || ""}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const {
    canViewProducts,
    canEditProducts,
    canPublishProducts,
    hasAnyEcommercePermission,
    getPermissionErrorMessage,
    isSuperAdmin,
  } = useEcommercePermissions();
  const hasGeneralAccess = hasAnyEcommercePermission();
  const hasProductAccess = canViewProducts();
  const featuredLimitReached =
    !isSuperAdmin && featuredProductsCount >= FEATURED_PRODUCTS_LIMIT;
  const featuredLimitMessage = `Limite de ${FEATURED_PRODUCTS_LIMIT} produits mis en avant atteinte. Retirez d'abord un produit de la sélection.`;
  const selectedFeaturedStore =
    featuredStores.find((store) => store.id === selectedFeaturedStoreId) || null;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(value);

  const isSellableProduct = (product: Product) => {
    const productType = String(product.product_type || "").toLowerCase();
    const productionKind = String(
      product.production_kind || "finished"
    ).toLowerCase();

    return (
      productType !== "raw" &&
      (productType !== "production" || productionKind === "finished")
    );
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      productsAbortRef.current?.abort();
    };
  }, []);

  // La mise en avant appartient à la boutique. On résout d'abord les boutiques
  // liées au magasin courant afin que le premier chargement utilise déjà la
  // même portée que la vitrine publique.
  useEffect(() => {
    const warehouseId = Number(selectedWarehouse);
    if (!hasProductAccess || !Number.isInteger(warehouseId) || warehouseId <= 0) {
      setFeaturedStores([]);
      setSelectedFeaturedStoreId(null);
      setFeaturedScopeWarehouseId(null);
      setFeaturedScopeError("");
      return undefined;
    }

    const controller = new AbortController();
    setFeaturedStores([]);
    setFeaturedScopeWarehouseId(null);
    setFeaturedScopeError("");

    const loadFeaturedStores = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/ecommerce/stores/catalog-display-order?warehouse_id=${warehouseId}`,
          {
            headers: ecommerceRequestHeaders(),
            signal: controller.signal,
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            data.error || "Impossible de déterminer la boutique liée."
          );
        }

        const stores: FeaturedStore[] = Array.isArray(data.stores)
          ? data.stores.map((store: FeaturedStore) => ({
              id: Number(store.id),
              store_name: store.store_name,
              store_slug: store.store_slug,
            }))
          : [];
        if (controller.signal.aborted) return;
        setFeaturedStores(stores);
        setSelectedFeaturedStoreId((current) =>
          stores.some((store) => store.id === current)
            ? current
            : stores[0]?.id ?? null
        );
      } catch (error: any) {
        if (!controller.signal.aborted && error?.name !== "AbortError") {
          setFeaturedStores([]);
          setSelectedFeaturedStoreId(null);
          setFeaturedScopeError(
            error?.message || "Impossible de déterminer la boutique liée."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setFeaturedScopeWarehouseId(warehouseId);
        }
      }
    };

    loadFeaturedStores();
    return () => controller.abort();
  }, [selectedWarehouse, selectedCompany, hasProductAccess, scopeRevision]);

  // 🔍 FETCH PRODUCTS WITH PAGINATION - useCallback to prevent infinite loops
  const fetchProducts = useCallback(
    async (page = cataloguePage, pageSize = cataloguePageSize) => {
      if (scopeKey !== scopeRef.current) return;
      const requestId = ++productsRequestIdRef.current;
      const requestScope = scopeKey;
      productsAbortRef.current?.abort();
      const controller = new AbortController();
      productsAbortRef.current = controller;
      const stale = () => !isMountedRef.current || controller.signal.aborted ||
        requestId !== productsRequestIdRef.current || requestScope !== scopeRef.current;
      if (!selectedWarehouse || featuredScopeWarehouseId !== Number(selectedWarehouse) ||
          featuredScopeError || !hasProductAccess) return;
      setLoading(true);
      setProductsError("");
      try {
        const params = new URLSearchParams({
          warehouse_id: String(selectedWarehouse), page: String(page), limit: String(pageSize),
          visibility: filters.visibility, featured: filters.featured,
        });
        if (selectedFeaturedStoreId) params.set("store_id", String(selectedFeaturedStoreId));
        if (filters.search) params.set("search", filters.search);
        if (filters.category) params.set("category", String(filters.category));
        const response = await fetch(`${API_URL}/api/produits/ecommerce?${params}`, {
          headers: ecommerceRequestHeaders(), signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (stale()) return;
        if (!response.ok) throw new Error(data.error || "Impossible de charger le catalogue.");
        if (!data.success || !Array.isArray(data.products)) throw new Error("Réponse du catalogue invalide.");
        setProducts(data.products.filter(isSellableProduct));
        setFeaturedProductsCount(Number(data.featured_count || 0));
        const total = Number(data.total) || 0;
        const loadedPageSize = Number(data.limit) || pageSize;
        setPaginationState({
          current: Math.min(Number(data.page) || page, Math.max(1, Math.ceil(total / loadedPageSize))),
          pageSize: loadedPageSize, total,
        });
      } catch (error: any) {
        if (!stale()) {
          setProducts([]);
          setPaginationState((previous) => ({ ...previous, total: 0 }));
          setProductsError(error.message || "Connexion au catalogue impossible.");
        }
      } finally {
        if (!stale()) setLoading(false);
      }
    },
    [selectedWarehouse, scopeKey, selectedFeaturedStoreId, featuredScopeWarehouseId,
      featuredScopeError, filters, cataloguePage, cataloguePageSize, hasProductAccess]
  );

  // 📂 FETCH CATEGORIES - Must be before conditional returns
  const fetchCategories = useCallback(async () => {
    const requestId = categoriesRequestIdRef.current + 1;
    categoriesRequestIdRef.current = requestId;
    const isStaleRequest = () =>
      !isMountedRef.current || requestId !== categoriesRequestIdRef.current;

    if (!selectedCompany) {
      if (!isStaleRequest()) {
        setCategories([]);
      }
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/categories?company_id=${selectedCompany}`,
        {
          headers: ecommerceRequestHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (!isStaleRequest()) {
          setCategories(data);
        }
      }
    } catch (error) {
      if (!isStaleRequest()) {
        console.error("❌ CATEGORIES FETCH ERROR:", error);
      }
    }
  }, [selectedCompany]);

  const handleTableChange = useCallback((page: number, pageSize?: number) => {
    setPaginationState((previous) => ({ ...previous, current: page, pageSize: pageSize || previous.pageSize }));
  }, []);

  useEffect(() => {
    setPaginationState((previous) => previous.current === 1 ? previous : { ...previous, current: 1 });
  }, [filters.search, filters.category, filters.visibility, filters.featured]);

  useEffect(() => {
    setProducts([]);
    setFeaturedProductsCount(0);
    setPaginationState((previous) => ({ ...previous, current: 1, total: 0 }));
    setProductsError("");
    setBatchResult(null);
    setModalVisible(false);
    setEditingProduct(null);
    productsAbortRef.current?.abort();
  }, [selectedCompany, selectedWarehouse, selectedFeaturedStoreId]);

  useEffect(() => {
    setFilters({ search: "", category: "", visibility: "all", featured: "all" });
  }, [selectedCompany, selectedWarehouse]);

  useEffect(() => {
    setSelectedProductIds([]);
  }, [selectedCompany, selectedWarehouse, selectedFeaturedStoreId, cataloguePage,
    cataloguePageSize, filters.search, filters.category, filters.visibility, filters.featured]);

  useEffect(() => {
    setCategories([]);
    categoriesRequestIdRef.current += 1;
    if (hasProductAccess && selectedWarehouse) void fetchCategories();
  }, [fetchCategories, hasProductAccess, selectedWarehouse]);

  useEffect(() => {
    if (!selectedWarehouse || featuredScopeWarehouseId !== Number(selectedWarehouse) ||
        featuredScopeError || !hasProductAccess) {
      setProducts([]);
      setFeaturedProductsCount(0);
      setLoading(false);
      setPaginationState((previous) => previous.total ? { ...previous, total: 0 } : previous);
      return;
    }
    void fetchProducts();
  }, [fetchProducts, selectedWarehouse, featuredScopeWarehouseId, featuredScopeError, hasProductAccess, settingsRevision]);

  // ⚠️ VÉRIFICATIONS DE PERMISSION


  // Rendu conditionnel pour les permissions
  if (!hasGeneralAccess) {
    return (
      <PermissionDenied
        title="Accès au module E-commerce refusé"
        subTitle="Permissions e-commerce requises"
        action="gérer les produits en ligne"
        module="Gestion E-commerce"
      />
    );
  }

  if (!hasProductAccess) {
    return (
      <PermissionDenied
        title="Gestion des produits en ligne refusée"
        subTitle="Permission spécifique requise"
        action="gérer les produits en ligne"
        module="Fiches Produit E-commerce"
        description="Vous devez avoir la permission 'Gestion Commerciale.Ecommerce.Configuration.FichesProduit.view' pour accéder à cette page."
      />
    );
  }

  const handleEdit = (product: Product) => {
    if (!canEditProducts()) {
      message.error(getPermissionErrorMessage("modifier", "fiche produit e-commerce"));
      return;
    }
    if (onEditProduct) onEditProduct(product, selectedFeaturedStore);
    else { setEditingProduct(product); setModalVisible(true); }
  };

  const runProductUpdate = async (ids: number[], patch: ProductOnlinePatch) => {
    if (productMutationBusyRef.current || loading || !ids.length) return;
    if (patch.online_visibility !== undefined && !canPublishProducts()) {
      message.error(getPermissionErrorMessage("publier/dépublier", "produit e-commerce"));
      return;
    }
    if (patch.online_featured !== undefined && !canEditProducts()) {
      message.error(getPermissionErrorMessage("modifier", "produit e-commerce"));
      return;
    }
    const selected = products.filter((product) => ids.includes(product.id));
    if (selected.length !== new Set(ids).size) return;
    if (patch.online_featured && selected.some((product) => !product.online_visibility)) {
      message.warning("Publiez les produits sélectionnés avant de les mettre en avant.");
      return;
    }
    const newFeaturedCount = selected.filter((product) => !product.online_featured).length;
    if (patch.online_featured && !isSuperAdmin && featuredProductsCount + newFeaturedCount > FEATURED_PRODUCTS_LIMIT) {
      message.warning(featuredLimitMessage);
      return;
    }
    const requestScope = scopeRef.current;
    productMutationBusyRef.current = true;
    setBulkActionLoading(true);
    setBatchResult(null);
    try {
      const result = await updateSelectedProducts({
        ids, patch, warehouseId: Number(selectedWarehouse), storeId: selectedFeaturedStoreId,
        shouldContinue: () => isMountedRef.current && requestScope === scopeRef.current,
      });
      if (!isMountedRef.current || requestScope !== scopeRef.current) return;
      setSelectedProductIds((current) => current.filter((id) => !result.updatedIds.includes(id)));
      setBatchResult({
        type: result.failures.length || result.skippedIds.length ? "warning" : "success",
        message: `${result.updatedIds.length} produit(s) actualisé(s)${result.failures.length ? ` · ${result.failures.length} échec(s)` : ""}${result.skippedIds.length ? ` · ${result.skippedIds.length} non traité(s)` : ""}`,
        description: result.failures.length
          ? result.failures.map((failure) => `${products.find((product) => product.id === failure.id)?.name || failure.id} : ${failure.message}`).join(" ")
          : result.skippedIds.length ? "Les produits non traités restent sélectionnés. Vérifiez vos droits avant de réessayer."
          : "Seuls les produits concernés ont été modifiés.",
      });
      await fetchProducts();
    } catch (error: any) {
      if (requestScope === scopeRef.current) message.error(error.message || "Mise à jour impossible.");
    } finally {
      productMutationBusyRef.current = false;
      if (isMountedRef.current) setBulkActionLoading(false);
    }
  };

  const toggleOnlineVisibility = (id: number, visible: boolean) =>
    runProductUpdate([id], { online_visibility: visible });
  const toggleFeatured = (id: number, featured: boolean) =>
    runProductUpdate([id], { online_featured: featured });
  const selectedProducts = products.filter((product) => selectedProductIds.includes(product.id));
  const controlsDisabled = loading || bulkActionLoading || Boolean(featuredScopeError);
  const featureSelectedDisabled = !canEditProducts() || controlsDisabled ||
    selectedProducts.some((product) => !product.online_visibility) ||
    (!isSuperAdmin && featuredProductsCount + selectedProducts.filter((product) => !product.online_featured).length > FEATURED_PRODUCTS_LIMIT);
  const confirmHideSelection = () => {
    if (!canPublishProducts() || controlsDisabled || !selectedProducts.length) return;
    const ids = selectedProducts.map((product) => product.id);
    const requestScope = scopeRef.current;
    Modal.confirm({
      title: `Masquer ${ids.length} produit(s) sélectionné(s) ?`,
      content: "Seules les fiches sélectionnées et leurs déclinaisons seront retirées de la vente en ligne, dans toutes les boutiques qui les proposent. Le stock et les autres produits ne seront pas modifiés.",
      okText: "Masquer la sélection", cancelText: "Annuler",
      onOk: () => requestScope === scopeRef.current ? runProductUpdate(ids, { online_visibility: false }) : undefined,
    });
  };

  const getStockModeLabel = (mode?: string) =>
    mode === "unlimited" ? "Illimité" : mode === "pre_order" ? "Sur commande" : "Standard";
  const productMetadata = (product: Product) =>
    [product.category_name, product.brand_name, product.is_variant_parent
      ? `${product.variants_count || 0} déclinaison${(product.variants_count || 0) > 1 ? "s" : ""}` : ""].filter(Boolean).join(" · ");
  const productPrice = (product: Product) =>
    product.is_variant_parent && product.price_min !== undefined && product.price_max !== undefined && product.price_min !== product.price_max
      ? `${formatCurrency(product.price_min)} – ${formatCurrency(product.price_max)}`
      : formatCurrency(product.price);
  const publicProductUrl = (product: Product) => selectedFeaturedStore
    ? `/store/${encodeURIComponent(selectedFeaturedStore.store_slug)}/product/${product.id}` : undefined;
  const visibilityControl = (product: Product) => (
    <span className="ecommerce-workspace__visibility">
      <Switch checked={product.online_visibility} aria-label={`Visibilité de ${product.name}`}
        disabled={!canPublishProducts() || controlsDisabled}
        onChange={(value) => void toggleOnlineVisibility(product.id, value)} />
      <span>{product.online_visibility ? "Visible" : "Masqué"}</span>
    </span>
  );
  const featuredControl = (product: Product) => (
    <Tooltip title={!canEditProducts() ? "Permission de modification requise." :
      !product.online_featured && !product.online_visibility ? "Publiez ce produit avant de le mettre en avant." :
      !product.online_featured && featuredLimitReached ? featuredLimitMessage : product.online_featured ? "Retirer de la mise en avant" : "Mettre en avant"}>
      <Button type="text" className={`ecommerce-workspace__star${product.online_featured ? " is-featured" : ""}`}
        icon={product.online_featured ? <StarFilled /> : <StarOutlined />}
        aria-label={`Mise en avant de ${product.name}`} aria-pressed={product.online_featured}
        disabled={!canEditProducts() || controlsDisabled || (!product.online_featured && (!product.online_visibility || featuredLimitReached))}
        onClick={() => void toggleFeatured(product.id, !product.online_featured)} />
    </Tooltip>
  );
  const productActions = (product: Product) => {
    const url = publicProductUrl(product);
    const items = [
      ...(canPublishProducts() ? [{ key: "visibility", label: product.online_visibility ? "Masquer ce produit" : "Publier en ligne",
        disabled: controlsDisabled, onClick: () => void toggleOnlineVisibility(product.id, !product.online_visibility) }] : []),
      ...(url && product.online_visibility ? [{ key: "view", label: <a href={url} target="_blank" rel="noopener noreferrer">Voir dans la boutique</a> }] : []),
    ];
    return <Space size={8}>
      {canEditProducts() && <Tooltip title="Modifier les paramètres en ligne"><Button
        icon={<EditOutlined />} aria-label={`Modifier ${product.name}`}
        disabled={controlsDisabled} onClick={() => handleEdit(product)} /></Tooltip>}
      {!!items.length && <Dropdown menu={{ items }} trigger={["click"]}>
        <Button icon={<MoreOutlined />} aria-label={`Actions pour ${product.name}`} disabled={controlsDisabled} />
      </Dropdown>}
    </Space>;
  };

  const columns = [
    { title: "Produit", key: "product", width: 350, render: (product: Product) => (
      <div className="ecommerce-workspace__product">
        <ProductThumbnail src={product.image_url} name={product.name} />
        <div className="ecommerce-workspace__product-copy"><Tooltip title={product.name}>
          <span className="ecommerce-workspace__product-name">{product.name}</span></Tooltip>
          <span className="ecommerce-workspace__metadata">{productMetadata(product)}</span>
        </div>
      </div>
    ) },
    { title: "Stock", key: "stock", width: 120, render: (product: Product) => <div>
      <strong>{product.stock_quantity}</strong><span className="ecommerce-workspace__metadata">{getStockModeLabel(product.stock_management_mode)}</span>
    </div> },
    { title: "Prix établissement", key: "price", width: 175, align: "right" as const,
      render: (product: Product) => <span className="ecommerce-workspace__money ecommerce-workspace__price-range">{productPrice(product)}</span> },
    { title: "Prix en ligne", key: "online_price", width: 145, align: "right" as const,
      render: (product: Product) => <span className="ecommerce-workspace__money">{formatCurrency(product.online_price ?? product.price)}</span> },
    { title: "Visibilité", key: "visibility", width: 140, render: visibilityControl },
    { title: "Mise en avant", key: "featured", width: 120, align: "center" as const, render: featuredControl },
    { title: "Actions", key: "actions", width: 105, fixed: "right" as const, render: productActions },
  ];

  const productCards = products.map((product) => <Card key={product.id} className="ecommerce-workspace__card" size="small">
    <div className="ecommerce-workspace__card-head">
      <Checkbox checked={selectedProductIds.includes(product.id)} disabled={controlsDisabled || (!canEditProducts() && !canPublishProducts())}
        aria-label={`Sélectionner ${product.name}`}
        onChange={(event) => setSelectedProductIds((ids) => event.target.checked ? [...ids, product.id] : ids.filter((id) => id !== product.id))} />
      <div className="ecommerce-workspace__product">
        <ProductThumbnail src={product.image_url} name={product.name} />
        <div><strong className="ecommerce-workspace__product-name">{product.name}</strong><span className="ecommerce-workspace__metadata">{productMetadata(product)}</span></div>
      </div>
      {productActions(product)}
    </div>
    <div className="ecommerce-workspace__card-foot">
      <div><span className="ecommerce-workspace__metadata">Prix établissement</span>{productPrice(product)}</div>
      <div><span className="ecommerce-workspace__metadata">Prix en ligne</span>{formatCurrency(product.online_price ?? product.price)}</div>
      <div><span className="ecommerce-workspace__metadata">Stock · {getStockModeLabel(product.stock_management_mode)}</span>{product.stock_quantity}</div>
      {visibilityControl(product)}{featuredControl(product)}
    </div>
  </Card>);

  if (!selectedWarehouse) {
    return <div className="ecommerce-workspace" style={workspaceStyle}>
      <Empty description="Sélectionnez un établissement pour gérer son catalogue e-commerce." />
    </div>;
  }

  return (
    <div className="ecommerce-workspace ecommerce-workspace--catalogue" style={workspaceStyle} role="region" aria-label="Catalogue e-commerce">
      <div className="ecommerce-workspace__breadcrumb">E-commerce / Catalogue produits</div>
      <header className="ecommerce-workspace__header">
        <div><Title level={1}>Catalogue e-commerce</Title>
          <div className="ecommerce-workspace__subtitle">Gérez la visibilité, les prix en ligne et la mise en avant de vos produits.</div></div>
        <div className="ecommerce-workspace__header-actions">
          <div className="ecommerce-workspace__store"><label htmlFor="catalogue-store">Boutique</label>
            <Select id="catalogue-store" value={selectedFeaturedStoreId} disabled={bulkActionLoading || !featuredStores.length}
              placeholder="Catalogue de l'établissement" onChange={setSelectedFeaturedStoreId}
              options={featuredStores.map((store) => ({ value: store.id, label: store.store_name }))} /></div>
          <Button icon={<ExportOutlined />} href={selectedFeaturedStore ? `/store/${encodeURIComponent(selectedFeaturedStore.store_slug)}` : undefined}
            target="_blank" rel="noopener noreferrer" disabled={!selectedFeaturedStore}>Voir la boutique</Button>
        </div>
      </header>
      <div className="ecommerce-workspace__filters">
        <EcommerceFilterField id="catalogue-search" label="Recherche">
          <Input id="catalogue-search" disabled={bulkActionLoading} prefix={<SearchOutlined />} placeholder="Rechercher un produit…" allowClear value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} />
        </EcommerceFilterField>
        <EcommerceFilterField id="catalogue-category" label="Catégorie">
          <Select id="catalogue-category" disabled={bulkActionLoading} value={filters.category} onChange={(value) => setFilters((current) => ({ ...current, category: value }))}
            options={[{ value: "", label: "Toutes les catégories" }, ...categories.map((category) => ({ value: String(category.id), label: category.name }))]} />
        </EcommerceFilterField>
        <EcommerceFilterField id="catalogue-visibility" label="Visibilité">
          <Select id="catalogue-visibility" disabled={bulkActionLoading} value={filters.visibility} onChange={(value) => setFilters((current) => ({ ...current, visibility: value }))}
            options={[{ value: "all", label: "Tous les produits" }, { value: "visible", label: "Visibles en ligne" }, { value: "hidden", label: "Masqués" }]} />
        </EcommerceFilterField>
        <EcommerceFilterField id="catalogue-featured" label="Mise en avant">
          <Select id="catalogue-featured" disabled={bulkActionLoading} value={filters.featured} onChange={(value) => setFilters((current) => ({ ...current, featured: value }))}
            options={[{ value: "all", label: "Tous" }, { value: "featured", label: "Mis en avant" }, { value: "not_featured", label: "Non mis en avant" }]} />
        </EcommerceFilterField>
        <Button type="text" icon={<ReloadOutlined />} disabled={bulkActionLoading || (!filters.search && !filters.category && filters.visibility === "all" && filters.featured === "all")}
          onClick={() => setFilters({ search: "", category: "", visibility: "all", featured: "all" })}>Réinitialiser</Button>
      </div>
      <div className="ecommerce-workspace__results">
        <span>{loading ? "Chargement du catalogue…" : productsError || featuredScopeError ? "Catalogue indisponible" : `${paginationState.total} résultats · ${products.length} sur cette page`}</span>
        <span>La sélection est limitée à cette page</span>
      </div>
      <div className="ecommerce-workspace__scope ecommerce-workspace__catalogue-scope">
        <div>{!loading && !productsError && !featuredScopeError && <span>Mise en avant : {featuredProductsCount}{!isSuperAdmin && ` / ${FEATURED_PRODUCTS_LIMIT}`} produit(s) · {selectedFeaturedStore ? "ensemble de la boutique" : "établissement sélectionné"}.</span>}
          <Tooltip title="La visibilité du produit est partagée avec ses déclinaisons et les boutiques qui le proposent."><span> Visibilité partagée.</span></Tooltip></div>
        <ProductDisplayOrderSettings warehouseId={Number(selectedWarehouse)} canEdit={canEditProducts()} />
      </div>
      {featuredScopeError && <Alert type="error" showIcon className="ecommerce-workspace__error" message={featuredScopeError}
        action={<Button onClick={() => setScopeRevision((revision) => revision + 1)}>Réessayer</Button>} />}
      {productsError && <Alert type="error" showIcon className="ecommerce-workspace__error" message={productsError}
        action={<Button onClick={() => void fetchProducts()}>Réessayer</Button>} />}
      {batchResult && <Alert className="ecommerce-workspace__error" type={batchResult.type} showIcon closable
        onClose={() => setBatchResult(null)} message={batchResult.message} description={batchResult.description} />}
      {!!selectedProducts.length && <div className="ecommerce-workspace__batch" role="region" aria-label="Actions sur les produits sélectionnés">
        <div><strong>{selectedProducts.length} produit(s) sélectionné(s)</strong><span>Actions uniquement sur la sélection</span></div>
        <Space wrap size={8}>
          <Button type="primary" icon={<UploadOutlined />} loading={bulkActionLoading}
            disabled={!canPublishProducts() || controlsDisabled}
            onClick={() => void runProductUpdate(selectedProducts.map((product) => product.id), { online_visibility: true })}>Publier en ligne</Button>
          <Button icon={<EyeInvisibleOutlined />} disabled={!canPublishProducts() || controlsDisabled} onClick={confirmHideSelection}>Masquer</Button>
          <Dropdown trigger={["click"]} menu={{ items: [
            { key: "feature", label: "Mettre en avant", disabled: featureSelectedDisabled,
              onClick: () => void runProductUpdate(selectedProducts.map((product) => product.id), { online_featured: true }) },
            { key: "unfeature", label: "Retirer de la mise en avant", disabled: !canEditProducts() || controlsDisabled,
              onClick: () => void runProductUpdate(selectedProducts.map((product) => product.id), { online_featured: false }) },
          ] }}><Button icon={<StarOutlined />} disabled={!canEditProducts() || controlsDisabled}>Mise en avant <DownOutlined /></Button></Dropdown>
          <Button type="text" icon={<CloseOutlined />} disabled={bulkActionLoading} onClick={() => setSelectedProductIds([])}>Annuler la sélection</Button>
        </Space>
      </div>}
      {isMobile ? <div className="ecommerce-workspace__cards">
        {loading ? <div role="status">Chargement…</div> : productCards.length ? productCards : <Empty description="Aucun produit ne correspond à vos filtres." />}
      </div> : <div className="ecommerce-workspace__table" ref={tableRef}>
        <Table dataSource={products} columns={columns} rowKey="id" loading={loading} pagination={false}
          scroll={{ x: 1180, y: tableHeight }}
          locale={{ emptyText: productsError ? "Chargement impossible" : "Aucun produit ne correspond à vos filtres." }}
          rowSelection={canEditProducts() || canPublishProducts() ? {
            selectedRowKeys: selectedProductIds, columnWidth: 44, preserveSelectedRowKeys: false,
            onChange: (keys) => setSelectedProductIds(keys.map(Number)),
            getCheckboxProps: (product: Product) => ({ disabled: controlsDisabled, name: product.name, "aria-label": `Sélectionner ${product.name}` }),
          } : undefined} />
      </div>}
      <div className="ecommerce-workspace__pagination">
        <span>{paginationState.total ? `${(paginationState.current - 1) * paginationState.pageSize + 1}–${Math.min(paginationState.current * paginationState.pageSize, paginationState.total)} sur ${paginationState.total} résultats` : "0 résultat"}</span>
        <Pagination current={paginationState.current} pageSize={paginationState.pageSize} total={paginationState.total}
          disabled={loading || bulkActionLoading} showSizeChanger pageSizeOptions={["10", "20", "50", "100"]} onChange={handleTableChange} />
      </div>

      {!onEditProduct && modalVisible && editingProduct && <ProductOnlineSettingsModal product={editingProduct} store={selectedFeaturedStore}
        onClose={() => { setModalVisible(false); setEditingProduct(null); }} onSaved={() => {
          setModalVisible(false); setEditingProduct(null); message.success("Fiche produit mise à jour."); void fetchProducts();
        }} />}

    </div>
  );
};

export default ProduitsEnLigne;
